import config from "@/config";
import * as parser from "@/wc/registry/lib/service/repository/parsers.js";
import type {
    IRepositoryContainer,
    IFields,
    IDynamicItemAction,
    IRepository,
    IRepositoryParameters,
} from "@/wc/registry/lib/service/repository/types";
import serviceRepository from "@/wc/registry/lib/service/repository/index.js";
import { monitoring } from "@/lib/integrations/index.js";
import { RepositoryCreationError } from "@/wc/registry/lib/service/repository/errors.js";
import {
    findRepositoriesTargetingPathUsingMethod,
    findRepositoriesWithSameLengthPathUsingMethod,
} from "@/wc/registry/lib/service/repository/utils.js";

// Converts e.g. /people/62/punch to a regexp that matches
// /people/{any_id}/punch.
function convertDynamicItemActionHrefToRepositoryPathnameRegExp(href: string) {
    return new RegExp(href.replace(/[0-9]+/g, "{(.+)}") + "$");
}

function convertPathnameToValueFromHrefRegExp(href: string) {
    return new RegExp(href.replace(/\{(.+)\}/g, "(.+)") + "$");
}

// Ignore actions with specific 'operations'. These describe a standard
// but conditional action, such as updating or deleting the current item.
// Typically these standard actions take up standard places in the UI.
function onlyNonStandardActions(action: IDynamicItemAction) {
    return !("operation" in action && ["delete"].includes(action.operation));
}

function onlyStandardActions(action: IDynamicItemAction) {
    return "operation" in action && ["delete"].includes(action.operation);
}

const repositoriesForContext: { [contextKey: string]: IRepositoryContainer } =
    {};

interface IAction {
    repository: IRepository;
    name: string;
    title: string;
    repositoryAction: string;
    parameters: IRepositoryParameters;
}

// The ActionsManager is an abstraction on top of the repository service, which
// aims to provide a uniform way to deal with state-depending actions while
// keeping components implementation agnostic.
export class ActionsManager {
    repositoriesThatAreBeingCreated: { [signature: string]: Promise<void> } =
        {};
    events = new EventTarget();

    constructor(public contextKey: string) {
        repositoriesForContext[contextKey] = {};
    }

    // A small helper container to provide quick access to the repositories that
    // are relevant for this context.
    get repositoryContainer(): IRepositoryContainer {
        return repositoriesForContext[this.contextKey];
    }

    // 'Standard' actions are actions that can have a standard place in the UI,
    // such as a 'delete' button. This function collects all the standard actions
    // that can be performed on a specific item, and checks whether the
    // repository that is linked to this action is actually capable of performing
    // this action.
    collectStandardActionsForItem(itemActions: IDynamicItemAction[]) {
        itemActions.filter(onlyStandardActions).forEach((itemAction) => {
            if (itemAction.operation === "delete") {
                const repositoryToUseForDeletion = serviceRepository.get(
                    this.contextKey
                );
                if (!repositoryToUseForDeletion) {
                    monitoring.error(
                        `Could not find a 'delete' action for the '${itemAction.name}' on the '${this.contextKey}' repository.`
                    );
                    return;
                }

                const repositoryToUseForDeletionAccordingToAction =
                    this.getRepositoryForAction(itemAction);

                if (
                    repositoryToUseForDeletion !==
                    repositoryToUseForDeletionAccordingToAction
                ) {
                    monitoring.error(
                        `The repository for the 'delete' action on the '${itemAction.name}' does not match the repository for the '${this.contextKey}' repository.`
                    );
                    return;
                }

                if (
                    !(
                        itemAction.operation in
                        repositoryToUseForDeletion.actionPaths
                    )
                ) {
                    monitoring.error(
                        `The '${itemAction.name}' repository does not support the 'delete' action.`
                    );
                    return;
                }
            }
        });
    }

    // Components can call this function with their data, and the ActionsManager
    // will figure out which actions are available and link the right
    // repositories for each of these actions.
    async collectActionsForItems(rows: IFields[]): Promise<void> {
        if (!config.openApiFileLocation) {
            monitoring.error(
                `${this.contextKey} - Can't configure openapi repositories for item actions. Please add the 'openApiFileLocation' to the application configuration.`
            );
            return;
        }

        rows.forEach((row) => {
            // Nothing to do if there are no actions defined for this row.
            if (!("_actions" in row)) {
                return;
            }
            const itemActions = row._actions as unknown as IDynamicItemAction[];
            this.collectStandardActionsForItem(itemActions);
            itemActions.filter(onlyNonStandardActions).forEach((itemAction) => {
                const signature = itemAction.name;
                // When a repository for this itemAction signature is being created, no
                // need to create it another time.
                if (signature in this.repositoriesThatAreBeingCreated) {
                    return;
                }

                // If a reference to the repository already exists in this table,
                // there is no need to create it another time.
                if (signature in this.repositoryContainer) {
                    return;
                }

                // Figure out if a repository already exists for this itemAction.
                const repository = this.getRepositoryForAction(itemAction);
                if (repository) {
                    this.repositoryContainer[signature] = repository;
                    this.addEventListeners(repository);
                    return;
                }

                const repositoryName = `${this.contextKey}-${itemAction.name.replaceAll(
                    "_",
                    "-"
                )}`;

                const regExp =
                    convertDynamicItemActionHrefToRepositoryPathnameRegExp(
                        itemAction.href
                    );
                // If there is no reference to a repository yet for this itemAction,
                // generate a new repository and add it to both the global repository
                // service and also add a reference to it in the local repository
                // container to support quick lookups.
                this.repositoriesThatAreBeingCreated[signature] = parser
                    .createRepositoryFromOpenApiSpec(
                        repositoryName,
                        config.openApiFileLocation,
                        [regExp]
                    )
                    .then((repository) => {
                        try {
                            serviceRepository.addRepository(
                                repositoryName,
                                repository
                            );
                            this.repositoryContainer[signature] = repository;
                            this.addEventListeners(repository);
                        } catch (e) {
                            console.error(
                                `Failed to add repository '${repositoryName}' to the repository service.`
                            );
                        }
                    })
                    .catch((e) => {
                        if (e instanceof RepositoryCreationError) {
                            console.error(
                                `Failed to create repository for action '${itemAction.name}' from the openapi spec. Try adding them to the repository service manually instead.`
                            );
                        } else {
                            throw e;
                        }
                    });
            });
        });

        await Promise.all(Object.values(this.repositoriesThatAreBeingCreated));
    }

    addEventListeners(repository: IRepository) {
        repository.events.addEventListener("created", this);
        repository.events.addEventListener("updated", this);
        repository.events.addEventListener("deleted", this);
    }

    // Proxy events from the repository through the action manager event target,
    // so that parties can easily listen to "action events".
    handleEvent(e: CustomEvent) {
        this.events.dispatchEvent(
            new CustomEvent(e.type, { detail: e.detail })
        );
    }

    // Given the href of a specific action, convert it to a RegExp which is
    // most likely to match a registered repository.
    //
    // A given href might look like:
    //   /tenant/abc-123/user/def-456/make-admin, where a repository might target
    //   /tenant/{tenant_id}/user/{user_id}/make-admin.
    //
    // This function will find the best matching repository for the given href
    // through the following steps:
    //  1. Find all repositories that target a similar path length-wise
    //       - Using the amount of slashes
    //  2. Find the repository that has the most overlap with the original href
    //     - Using the amount of matching segments
    //  3. Reconstruct the href using the best matching repository path, but
    //   replace the segments that do not overlap with "(.+)".
    //
    // After above steps on the example, the RegExp will look like this:
    //  /tenant/{(.+)}/user/{(.+)}/make-admin, which can then be used to search
    //  the repository registry for a matching repository.
    convertActionToRegExpToSearchRepositories(action: IDynamicItemAction) {
        const matchingLocalRepositories =
            findRepositoriesWithSameLengthPathUsingMethod(
                this.repositoryContainer,
                action.href,
                action.method
            );

        const matchingGlobalRepositories =
            serviceRepository.findRepositoriesWithSameLengthPathUsingMethod(
                action.href,
                action.method
            );

        const matchingRepositories = [
            ...matchingLocalRepositories,
            ...matchingGlobalRepositories,
        ];

        const repos: { path: string; repository: IRepository }[] = [];
        matchingRepositories.forEach((repository) => {
            Object.values(repository.actionPaths).forEach((actionPath) => {
                if (actionPath.method === action.method) {
                    repos.push({ path: actionPath.path, repository });
                }
            });
        });

        // Find the entry which has the most overlap with the original href
        let bestMatch = null;
        let bestMatchLength = 0;
        const hrefSegments = action.href.split("/");
        const minimumOverlap = Math.ceil(hrefSegments.length / 2);
        repos.forEach((repo) => {
            const pathSegments = repo.path.split("/");
            const overlap = pathSegments.filter((segment, index) => {
                return segment === hrefSegments[index];
            }).length;

            if (overlap > bestMatchLength && overlap >= minimumOverlap) {
                bestMatch = repo;
                bestMatchLength = overlap;
            }
        });

        if (bestMatch) {
            // Reconstruct the href using the best matching repository path,
            // but replace the segments that do not overlap with "(.+)"
            const bestMatchSegments = bestMatch.path.split("/");
            const newHref = hrefSegments
                .map((segment, index) => {
                    return bestMatchSegments[index] === segment
                        ? segment
                        : "{(.+)}";
                })
                .join("/");

            return RegExp(newHref + "$");
        }

        return undefined;
    }

    getRepositoryForAction(action: IDynamicItemAction): IRepository {
        const possiblePathRegExp =
            this.convertActionToRegExpToSearchRepositories(action);
        if (!possiblePathRegExp) {
            monitoring.error(
                `Could not find a repository for action '${action.name}' and href '${action.href}'`
            );
            return;
        }
        const matchingLocalRepositories =
            findRepositoriesTargetingPathUsingMethod(
                this.repositoryContainer,
                possiblePathRegExp,
                action.method
            );
        if (matchingLocalRepositories.length > 0) {
            return matchingLocalRepositories[0];
        }

        // If it doesn't exist in the table yet, look for a repository with the
        // action signature in the (global) repository registry. If it does
        // exist add it to the temporary repository registry.
        const matchingGlobalRepositories =
            serviceRepository.findRepositoriesTargetingPathUsingMethod(
                possiblePathRegExp,
                action.method
            );
        if (matchingGlobalRepositories.length > 0) {
            return matchingGlobalRepositories[0];
        }
    }

    // Given a specific items, check which standard actions are allowed for
    // this item. This is useful for e.g. a table to know whether it has to
    // show a 'delete' button.
    getAllowedStandardActionsForItem(item: IFields): { delete: boolean } {
        const actions = item._actions as unknown as IDynamicItemAction[];
        const standardActions = actions.filter(onlyStandardActions);
        const isDeleteSupported = standardActions.some(
            (action) => action.operation === "delete"
        );
        const isDeleteAllowedForThisItem = standardActions.some(
            (action) => action.operation === "delete"
        );
        return {
            delete: isDeleteSupported && isDeleteAllowedForThisItem,
        };
    }

    // Given a specific item, strip away the implementation details and return
    // a list of actions, where each of these actions include the repositories
    // and details that are required to be able to execute the actions.
    getActionsForItem(item: IFields): IAction[] {
        const actions = item._actions as unknown as IDynamicItemAction[];
        return actions
            .filter(onlyNonStandardActions)
            .map((action) => {
                const repository = this.repositoryContainer[action.name];
                if (!repository) {
                    monitoring.error(
                        `Could not find repository for action '${action.name}' and href '${action.href}'`
                    );
                    return;
                }

                const repositoryAction =
                    "getActionForMethod" in repository
                        ? repository.getActionForMethod(action.method)
                        : "create";
                if (!repositoryAction) {
                    monitoring.error(
                        `Could not find action for method '${action.method}' in repository '${repository.name}'`
                    );
                    return;
                }

                const valueRegExp = convertPathnameToValueFromHrefRegExp(
                    repository.actionPaths[repositoryAction].path
                );
                const keys =
                    repository.actionPaths[repositoryAction].allParameters
                        .pathParameters;
                const values = valueRegExp.exec(action.href).slice(1);

                if (keys.length !== values.length) {
                    console.log(
                        `Could not find values for keys ${keys} in ${action.href}`
                    );
                    return;
                }

                const parameters: IRepositoryParameters = {};
                keys.forEach((key, index) => {
                    parameters[key] = values[index];
                });

                return {
                    repository: repository,
                    name: action.name,
                    title: action.title,
                    parameters,
                    repositoryAction,
                };
            })
            .filter((info) => info !== undefined);
    }
}
